import { ActiveStatuses } from "@/components/organisms/Drivers/MyDrivers/MyDrivers/types";
import { useGetFleetDriversQuery } from "@/redux/apis/driver/driverApi";

/**
 * Statuses that consume a driver seat on the operator's plan.
 *
 * Deliberately identical to the server's definition in
 * AccountBelongsToMapper::countActiveDriversByProviderKey() — `active` +
 * `Approved` — which is what actually enforces the limit on every add-driver
 * path (fleet invite, bulk invite, driver self-registration, provider attach).
 * If this list drifts from that one, the tab counter stops predicting whether
 * an invite will succeed.
 *
 * Everything else is excluded on purpose:
 *   - archived (`removed`, `left`, `Declined`, `Retired`, `Permanently
 *     Terminated`) — the `account_belongs_to` row survives removal, so counting
 *     these permanently inflates the total and blocks downgrades.
 *   - pending — an unapproved application isn't occupying a seat yet.
 *   - suspended — matches VEHICLE_VISIBILITY_EXIT_STATUSES on the vehicle side,
 *     which releases quota on suspension because nothing stops a suspended
 *     driver working for another operator.
 */
export const PlanCountedDriverStatuses = ActiveStatuses;

/**
 * Number of drivers consuming a seat on the operator's plan.
 *
 * Always go through this hook rather than querying `/fleet` directly for a
 * count: with no `account_status` the API counts every fleet relationship
 * whatever its status, which silently includes removed drivers.
 */
export const useFleetDriverCount = (operatorId?: string) => {
  const { data, isLoading } = useGetFleetDriversQuery(
    {
      provider_uuid: operatorId ?? "",
      page: "1",
      page_size: "1",
      account_status: PlanCountedDriverStatuses,
    },
    {
      skip: !operatorId,
    }
  );

  return {
    driverCount: data?.data.page.total ?? 0,
    isLoading,
  };
};
